import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Calendar } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BookLink } from "./BookLink";

const links = [
    { to: "/", label: "Home", testid: "nav-home-link" },
    { to: "/services", label: "Services", testid: "nav-services-link" },
    { to: "/about", label: "About", testid: "nav-about-link" },
    { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export const Logo = ({ stacked = false }) => (
    <Link to="/" data-testid="nav-logo" className="flex items-center group" aria-label="Chirolane & Wellness home">
        <img
            src={stacked ? "/assets/logo-stacked.png" : "/assets/logo-horizontal-dark.png"}
            alt="Chirolane & Wellness, LLC"
            className={`${stacked ? "h-24" : "h-10 sm:h-12"} w-auto transition-transform duration-300 group-hover:scale-[1.03]`}
        />
    </Link>
);

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header className="fixed top-0 inset-x-0 z-50 bg-white/75 backdrop-blur-xl border-b border-slate-200/60">
            <nav className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 sm:h-20 flex items-center justify-between" aria-label="Main navigation">
                <Logo />
                <div className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `text-sm font-medium tracking-wide transition-colors duration-200 ${
                                    isActive ? "text-blue-700" : "text-slate-600 hover:text-[#0A192F]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </div>
                <div className="flex items-center gap-3">
                    <BookLink
                        data-testid="nav-book-button"
                        className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white text-sm font-semibold px-5 py-2.5 transition-all duration-300 hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5"
                    >
                        <Calendar className="h-4 w-4" />
                        Book an Appointment
                    </BookLink>
                    <button
                        data-testid="mobile-menu-button"
                        className="md:hidden p-2 text-[#0A192F]"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </nav>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`mobile-${l.testid}`}
                                    className={({ isActive }) =>
                                        `font-display text-2xl ${isActive ? "text-blue-700" : "text-[#0A192F]"}`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
